import type { CheckboxListOptions } from "@dealer-center/core-components";
import {
  Alert,
  Box,
  Button,
  CheckBoxList,
  Typography
} from "@dealer-center/core-components";
import { ImportModalAccount } from "./account";
import { LoadingIndicator } from "@dealer-center/core-custom-components";
import { useInventoryConfigsGet } from "@dealer-center/ddc-pages-inventory-config-fetch";
import { useGetAccountId } from "@dealer-center/ddc-api-account-fetch";
import { checkIfConfigExists } from "./helper";

interface BulkImportModalMainProps {
  selectedAccount: string | null;
  setSelectedAccount: (account: string | null) => void;
  selectedConfigs: string[];
  setSelectedConfigs: (configs: string[]) => void;
}
export const BulkImportModalMain = ({
  selectedAccount,
  setSelectedAccount,
  selectedConfigs,
  setSelectedConfigs
}: BulkImportModalMainProps) => {
  const currentAccountIdId = useGetAccountId();
  const shouldShowInvalidAccount = selectedAccount === currentAccountIdId;

  const {
    data: currentConfigList,
    isLoading: isCurrentConfigListLoading,
    isError: isCurrentConfigListError
  } = useInventoryConfigsGet(currentAccountIdId);
  const {
    data: importedConfigList,
    isLoading: isImportedConfigListLoading,
    isError: isImportedConfigListError
  } = useInventoryConfigsGet(shouldShowInvalidAccount ? null : selectedAccount);

  if (isCurrentConfigListError) {
    return <Alert type="error">Error loading current configs</Alert>;
  }
  if (isImportedConfigListError) {
    return <Alert type="error">Error loading imported configs</Alert>;
  }

  const checkboxesToList: CheckboxListOptions[] =
    importedConfigList?.map(config => ({
      label: `${config} ${
        checkIfConfigExists(currentConfigList, config) ? "(Will Overwrite)" : ""
      }`,
      value: config
    })) || [];
  const shouldShowLoading =
    !shouldShowInvalidAccount &&
    (isCurrentConfigListLoading || isImportedConfigListLoading);

  const shouldShowNoConfigs =
    !shouldShowInvalidAccount &&
    !isCurrentConfigListLoading &&
    !isImportedConfigListLoading &&
    selectedAccount &&
    Array.isArray(checkboxesToList) &&
    checkboxesToList.length === 0;

  const shouldShowConfigs =
    !shouldShowInvalidAccount &&
    !isCurrentConfigListLoading &&
    !isImportedConfigListLoading &&
    selectedAccount &&
    Array.isArray(checkboxesToList) &&
    checkboxesToList.length > 0;

  return (
    <>
      <ImportModalAccount setSelectedAccount={setSelectedAccount} />
      {shouldShowLoading && (
        <LoadingIndicator actionName="Loading Configs" text="Loading Configs" />
      )}
      {shouldShowInvalidAccount && (
        <Alert type="error">Cannot Import Configs from the Same Account</Alert>
      )}
      {shouldShowNoConfigs && (
        <Alert type="info">No Configs Available to Import</Alert>
      )}
      {shouldShowConfigs && (
        <>
          <Typography sx={{ mb: 2 }} variant="h4">
            Importing from {selectedAccount}
          </Typography>
          <Box sx={{ mb: 2 }}>
            <Button
              analyticsConfiguration={{ eventName: "Select All" }}
              onClick={() => {
                const allConfigs = checkboxesToList.map(config => config.value);
                setSelectedConfigs(allConfigs);
              }}
            >
              Select All
            </Button>
            <Button
              analyticsConfiguration={{ eventName: "Deselect All" }}
              sx={{ ml: 2 }}
              onClick={() => {
                setSelectedConfigs([]);
              }}
            >
              Deselect All
            </Button>
          </Box>
          <CheckBoxList
            required
            label="Select Configs to Import"
            name="configList"
            options={checkboxesToList}
            values={selectedConfigs}
            onChange={value => {
              setSelectedConfigs(value.target.value);
            }}
          />
        </>
      )}
    </>
  );
};
